import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faEnvelope,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import './NotFound.scss';

/**
 * NotFound component - Custom 404 page for MET Connect® website
 * 
 * @returns {JSX.Element} The NotFound component
 */
const NotFound = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  /**
   * Handle return to home page
   */
  const handleHomeClick = () => {
    navigate('/');
  };

  /**
   * Handle contact button click - navigate to homepage and scroll to contact section
   */
  const handleContactClick = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  return (
    <div className="not-found-page">
      <div className="hero-section" 
        style={{
          backgroundImage: `linear-gradient(rgba(36, 36, 36, 0.1), rgba(36, 36, 36, 0.0)), url(${process.env.PUBLIC_URL}/images/not-found.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
        <div className="hero-overlay"></div>
        <Container className="hero-content">
          <Row className="align-items-center">
            <Col lg={7} className="hero-text">
              <h1 className="hero-title">
                404 - Page Not Found
              </h1>
            </Col>
            <div className="vertical-divider-container">
              <div className="vertical-divider"></div>
            </div>
            <Col>
              <p className="hero-subtitle">
                We couldn't find the page you were looking for. It might have been moved, 
                deleted, or never existed in the first place.
              </p>
              <div className="hero-buttons">
                <Button 
                  variant="primary" 
                  size="lg" 
                  onClick={handleHomeClick} 
                  className="animated-btn"
                >
                  <span className="animated-btn-content">
                    Return to Homepage
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </span>
                </Button>
              </div>
              <div className="hero-buttons mt-3">
                <Button 
                  variant="primary" 
                  size="lg" 
                  as={Link} 
                  to="/resources" 
                  className="animated-btn"
                >
                  <span className="animated-btn-content">
                    Browse Resources
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </span>
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="error-info-section">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <div className="content-block">
                <h2 className="text-center">What Went Wrong?</h2>
                <p className="text-center">
                  The page you're looking for doesn't exist or has been moved. 
                  This could be due to:
                </p>
                <ul className="error-list">
                  <li>A mistyped URL</li>
                  <li>An outdated bookmark or link</li>
                  <li>Content that has been moved or deleted</li>
                </ul>
                <p className="text-center">
                  You can use the navigation menu above to browse our website,
                  or click the button below to return to our homepage.
                </p>
                <div className="mt-4 text-center">
                  <Button 
                    variant="primary" 
                    size="lg" 
                    onClick={handleHomeClick} 
                    className="me-3"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Back to Homepage
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="lg" 
                    onClick={handleContactClick}
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                    Contact Us
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="cta-section">
        <Container className="text-center">
          <h3>Looking for something specific?</h3>
          <p>Our team is here to help you find what you need.</p>
          <Button className="btn btn-primary btn-lg" onClick={handleContactClick}>
            Get in Touch
          </Button>
        </Container>
      </section>
    </div>
  );
};

export default NotFound;